import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SectionParticles } from "@/components/SectionParticles";
import { SectionBg } from "@/components/SectionBg";
import { scrollToId } from "@/lib/scroll";

gsap.registerPlugin(ScrollTrigger);

const PARAGRAPH =
  "VA home loan information is often compressed into one phrase—“zero down”—while buyers are left with questions about eligibility, entitlement, credit, cash, the funding fee, the offer, and what happens after an application. This workshop separates those questions and places them in the Benefit-to-Keys sequence: Verify → Model → Move.";

const LINES = [
  { text: "AN EARNED BENEFIT", cls: "text-white" },
  { text: "SHOULD COME WITH", cls: "text-white/[0.34]" },
  { text: "A CLEAR MAP—", cls: "text-white" },
  { text: "NOT MORE MYTHS.", cls: "text-[var(--accent)]" },
];

/* SECTION 3 — MANIFESTO */
export const Manifesto = () => {
  const ref = useRef(null);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    const words = ref.current.querySelectorAll(".manifesto-word");
    const tween = gsap.fromTo(
      words,
      { opacity: 0.12 },
      {
        opacity: 1,
        stagger: 0.04,
        ease: "none",
        scrollTrigger: { trigger: ref.current, start: "top 75%", end: "top 20%", scrub: 0.5 },
      },
    );
    return () => {
      tween.scrollTrigger?.kill();
      tween.kill();
    };
  }, []);

  return (
    <section ref={ref} className="relative overflow-hidden px-5 md:px-10 lg:px-16 py-28 md:py-40" data-testid="manifesto-section">
      <SectionBg img="bg-home-5.jpg" strength={0.92} />
      <SectionParticles variant="houses" />
      <div className="relative z-10 max-w-6xl mx-auto text-left">
        <p className="font-mono text-[11px] tracking-[0.3em] text-[var(--muted)] mb-12">
          [001] — THE PROBLEM
        </p>
        <h2 className="font-display font-semibold text-4xl sm:text-6xl lg:text-[5.4rem] leading-[1.02] mb-14">
          {LINES.map((line, li) => (
            <span key={li} className={`block ${line.cls}`}>
              {line.text.split(" ").map((w, wi) => (
                <span key={wi} className="manifesto-word inline-block mr-[0.28em]">
                  {w}
                </span>
              ))}
            </span>
          ))}
        </h2>
        <p className="text-base md:text-lg leading-relaxed text-[rgba(255,255,255,0.78)] max-w-2xl mb-12">
          {PARAGRAPH}
        </p>
        <button
          data-testid="manifesto-cta-button"
          onClick={() => scrollToId("register")}
          className="glint border border-[var(--accent)] text-[var(--accent)] font-semibold text-sm tracking-[0.12em] px-8 py-4 rounded-[4px] hover:bg-[var(--accent)] hover:text-[var(--background-deep)] transition-colors duration-250"
        >
          BUILD MY CLEARER MAP
        </button>
      </div>
    </section>
  );
};
